// ONE SET OF WORDS FOR A NUMBER, USED ON EVERY SCREEN THAT SHOWS ONE.
//
// His instruction, 2026-10-14: *"every number surface must offer the same plain-language choices,
// and the words must say whether the number goes to the assembly or to the tag's component. Get
// rid of 'Add as other' and 'as something else…' — nobody knows what they mean."*
//
// Two screens showed numbers with two different sets of buttons: the Ask-AI read rows and the
// part's own number list. They drifted because each wrote its own labels. They now both import
// from here, so a wording change happens in ONE place and cannot drift again — and a test asserts
// neither screen hard-codes its own.
//
// THE RULE THE WORDS CARRY: a number read off a BOLT-ON COMPONENT'S tag can never be the
// assembly's own identity. The destination is in the button text, every time, so nobody has to
// remember which list they are adding to.

// What a value on a tag can be, in words a person uses out loud — never role codes on screen.
export const ROLE_WORDS = {
  service_part: "part number",
  casting_family: "casting number",
  vendor: "vendor's number",
  serial: "serial number",
  date_code: "date code",
  rating: "rating",
  customer: "customer number",
  quantity: "quantity",
  other: "number printed on it",
};

// Short chip wording for a saved number's role badge (the part's number list).
export const ROLE_CHIP = {
  service_part: "part #", casting_family: "casting #", vendor: "vendor #", serial: "serial",
  date_code: "date code", rating: "rating", customer: "customer #", quantity: "quantity",
  other: "other value",
};

// The pick-list, in the order a yard worker meets them. "service_part" is not here: a part number
// has its own button on every row.
export const PICK_ROLES = ["serial", "casting_family", "vendor", "date_code", "rating", "customer", "quantity", "other"];

// WHERE THE VALUE IS GOING, in words. `component` = read off a bolt-on's own tag.
export const destinationWords = (inComponent) => (inComponent ? "this component" : "the assembly");

const cap = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

// THE SAME FOUR CHOICES, ON EVERY NUMBER, ON EVERY SCREEN.
export function numberWords(inComponent) {
  const where = destinationWords(inComponent);
  return {
    where,
    partNumber: `Part number of ${where}`,
    partNumberDone: `Saved as ${where}'s part number`,
    serial: `Serial number of ${where}`,
    serialDone: `Saved as ${where}'s serial number`,
    anotherPartNumber: `Another part number on ${where}`,
    otherMakersPartNumber: `Another maker's part number on ${where}`,
    pick: `Pick what this number is…`,
    roleItem: (role) => `${cap(ROLE_WORDS[role] || role.replace("_", " "))} on ${where}`,
    keyword: `Search word for ${where}`,
    keywordDone: `Search word added`,
    promote: `Make this the assembly's part number`,
    promoteConfirm: `Yes — make it the assembly's part number`,
    photoHint: `The photo this number was read from — tap to open it`,
    // HOVER HELP (PC) AND LONG-PRESS HELP (tablet) — the same words in both places.
    help: {
      partNumber: `Saves this value as the part number of ${where}. It lands unverified — tap ✓verify once you have checked it against the stamp.`,
      serial: `Saves this value as the serial number of ${where}. It lands unverified until you check it.`,
      another: `Saves this as another part number on ${where} — a part can carry several.`,
      otherMakers: `This tag carries two makers' part numbers. Saves this one as a part number too, on ${where}.`,
      pick: `Say what this number is — serial, casting, vendor, date code, rating, customer, quantity — and save it on ${where}.`,
      keyword: `Adds this value as a search word for ${where}. Search words are free — no AI charge.`,
      promote: `Two taps. Makes this component's number the ASSEMBLY'S OWN part number — the one the part is sold under.`,
      copy: `Copies the value to the clipboard. Nothing is saved to the part.`,
    },
  };
}
